import {
  ChevronDownIcon,
  getEntityImage,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { Box, styled } from "@mui/material";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";

import { useFastCheckoutContext } from "@/components/Checkout/CheckoutMachine";
import {
  ACTION_REQUIRED_ERROR,
  FastCheckoutState,
} from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import {
  UserInteractionType,
  UserRetailerCheckoutOrderStatusV2,
} from "@/components/Checkout/types";
import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import { useAppSelector } from "@/redux/hooks";
import { Retailer } from "@/redux/types";
import { useSelector } from "@xstate/react";
import { useEffect, useState } from "react";
import EditConnectPopup from "../../CheckoutV2/CheckoutForm/components/retailerConnect/EditConnectPopup";
import { OTPPopup } from "../../CheckoutV2/CheckoutForm/components/retailerConnect/EditConnectPopup/OTP";

const AccountPreviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  fontSize: "16px",
  gap: "10px",
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
    gap: "7px",
  },
  ".logo": {
    width: "25px",
    height: "25px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: "20px",
      height: "20px",
    },
  },
}));

const AccountPreview = () => {
  const { actorRef } = useFastCheckoutContext();

  const retailerUsername = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo?.retailerConnection?.username
  );
  const retailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );
  const retailerAccount = retailerUsername ?? "Unknown";

  return (
    <AccountPreviewContainer>
      {retailerId ? (
        <img
          alt="logo"
          className="logo"
          src={getEntityImage(retailerId, "retailer")}
        />
      ) : null}
      <div className="title">{retailerAccount}</div>
    </AccountPreviewContainer>
  );
};

const AccountInputFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: "24px",
  },
  ".input-container": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "column",
      gap: "15px",
    },
  },
}));

const UpdateUserNamePassword = ({
  connectReward,
  currentRetailer,
}: {
  connectReward: number;
  currentRetailer?: Retailer;
}) => {
  const { send } = useFastCheckoutContext();

  const currentRetailerId = currentRetailer?.retailerId ?? 0;

  const openEdit = () => {
    EditConnectPopup.show({
      connectReward,
      onSaveCredentials: handleSubmit,
      currentUsername: "",
      currentPassword: "",
      retailer: currentRetailer,
      checkoutIntentUsername: "",
    });
  };

  useEffect(() => {
    if (currentRetailer) {
      openEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRetailer]);

  const handleSubmit = async (email: string, password: string) => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.RETAILER_CREDENTIALS,
        interactionData: {
          credential: {
            username: email,
            password: password,
          },
          retailerId: currentRetailerId,
        },
      },
    });
  };

  return (
    <AccountInputFormContainer>
      <Box display="flex" flexDirection="row" justifyContent="center">
        <OutlineButton
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
          }}
          onClick={openEdit}
        >
          <img
            src={getEntityImage(currentRetailerId, "retailer")}
            alt="Retailer Logo"
            style={{
              width: "24px",
              height: "24px",
            }}
          />
          Reconnect my {currentRetailer?.displayName} account
        </OutlineButton>
      </Box>
    </AccountInputFormContainer>
  );
};

const OTPEnter = ({ currentRetailer }: { currentRetailer?: Retailer }) => {
  const [otp, setOtp] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);

  const [resendCoolDown, setResendCoolDown] = useState(0);
  const { send } = useFastCheckoutContext();

  useEffect(() => {
    if (currentRetailer) {
      setOtpOpen(true);
    }
  }, [currentRetailer]);

  useEffect(() => {
    if (resendCoolDown > 0) {
      setTimeout(() => setResendCoolDown(resendCoolDown - 1), 1000);
    }
  }, [resendCoolDown]);

  const handleSubmit = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.OTP_VERIFICATION,
        interactionData: {
          otpCode: otp,
        },
      },
    });
  };

  const handleResend = () => {
    setResendCoolDown(30);
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.OTP_VERIFICATION,
        interactionData: {
          resend: true,
        },
      },
    });
  };

  const handleChange = (otp: string) => {
    setOtp(otp);
  };

  return (
    <AccountInputFormContainer>
      <OTPPopup
        open={otpOpen}
        onClose={() => {
          setOtpOpen(false);
        }}
        value={otp}
        onChange={handleChange}
        onSave={handleSubmit}
        retailer={currentRetailer}
        onResend={handleResend}
        resendCoolDown={resendCoolDown}
      />
      <Box display="flex" flexDirection="row" justifyContent="center">
        <OutlineButton
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            px: "60px",
          }}
          onClick={() => setOtpOpen(true)}
        >
          <img
            src={getEntityImage(currentRetailer?.retailerId ?? 0, "retailer")}
            alt="Retailer Logo"
            style={{
              width: "24px",
              height: "24px",
            }}
          />
          Enter OTP
        </OutlineButton>
      </Box>
    </AccountInputFormContainer>
  );
};

const ExperimentalAccountSection = () => {
  const { actorRef } = useFastCheckoutContext();
  const retailerId = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.retailerId
  );
  const userCheckoutInfo = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo
  );

  const order = useSelector(
    actorRef,
    (state) =>
      state.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const status = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );
  const accountSectionState = useSelector(
    actorRef,
    (state) => state.context.sectionState.account
  );
  const { open, chevronShow, errorMessage } = accountSectionState;
  const error = useSelector(actorRef, (state) => state.context.error);
  const errorType = error?.type;

  const retailers = useAppSelector(({ retailers }) => retailers);

  const retailer = retailers.find((r) => r.retailerId === retailerId);

  const connectReward = retailer?.retailerConnectRewardAmount ?? 0;
  const toggleOpen = () => {
    if (!chevronShow) {
      return;
    }
    if (open) {
      actorRef.send({
        type: "ON_SESSION_CLOSE",
        data: { sessionToClose: "account" },
      });
    } else {
      actorRef.send({
        type: "ON_SESSION_OPEN",
        data: { sessionToOpen: "account" },
      });
    }
  };

  const showError =
    order?.status === UserRetailerCheckoutOrderStatusV2.checkout_in_progress;

  // if there is no username, skip the account section for receipt generated state
  if (
    status === FastCheckoutState.RECEIPT_GENERATED &&
    !userCheckoutInfo?.retailerConnection?.username
  ) {
    return null;
  }

  const renderBody = () => {
    if (showError && errorType === ACTION_REQUIRED_ERROR.LOGIN_ERROR) {
      return (
        <UpdateUserNamePassword
          connectReward={connectReward}
          currentRetailer={retailer}
        />
      );
    }
    if (showError && errorType === ACTION_REQUIRED_ERROR.OTP_ERROR) {
      return <OTPEnter currentRetailer={retailer} />;
    }
    return <AccountPreview />;
  };

  return (
    <Box className="section">
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">{retailer?.displayName} Account</div>
        {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>
      <StoreAnimatedHeightDiv>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        {renderBody()}
      </StoreAnimatedHeightDiv>
    </Box>
  );
};

export default ExperimentalAccountSection;
